import Phaser from "phaser";
import { FONT_FAMILY, TYPO } from "../ui/uiTokens.js";
import { PALETTE } from "../themes/palette.js";
import { BrickMarkStatus } from "./BrickMarkStatus.js";
import { crackTextureFor } from "./brickAppearance.js";
import { DEPTH } from "./depthLayers.js";

type MaskableGameObject = Phaser.GameObjects.GameObject & {
  setMask: (mask: Phaser.Display.Masks.GeometryMask) => unknown;
  clearMask: () => unknown;
};

export class BrickVisualParts {
  readonly glow: Phaser.GameObjects.Graphics;
  readonly glowImage: Phaser.GameObjects.Image;
  readonly pressureDanger: Phaser.GameObjects.Graphics;
  readonly baseFrame: Phaser.GameObjects.Image;
  readonly overlay: Phaser.GameObjects.Image;
  readonly kindDetail: Phaser.GameObjects.Graphics;
  readonly mysteryIcon: Phaser.GameObjects.Image;
  readonly sparkle: Phaser.GameObjects.Graphics;
  readonly markStatus: BrickMarkStatus;
  readonly crack: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
  readonly healthBar: Phaser.GameObjects.Graphics;
  readonly shieldIcon: Phaser.GameObjects.Graphics;

  private readonly richVisuals: Phaser.GameObjects.GameObject[];
  mysteryGlyph?: Phaser.GameObjects.Text;

  constructor(
    private readonly scene: Phaser.Scene,
    defaultBaseKey: string,
    defaultOverlayKey: string,
    mysteryIconKey: string,
    forwardPointerDown: (...args: unknown[]) => void,
  ) {
    this.glow = scene.add
      .graphics()
      .setDepth(DEPTH.brickGlow)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setVisible(false);
    this.glowImage = scene.add.image(0, 0, defaultBaseKey).setDepth(DEPTH.brickGlow).setVisible(false);
    this.pressureDanger = scene.add
      .graphics()
      .setDepth(DEPTH.brickLabel + 0.2)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setVisible(false);
    this.baseFrame = scene.add.image(0, 0, defaultBaseKey).setDepth(DEPTH.brickBase).setVisible(false);
    this.overlay = scene.add.image(0, 0, defaultOverlayKey).setDepth(DEPTH.brickOverlay).setVisible(false);
    this.kindDetail = scene.add.graphics().setDepth(DEPTH.brickOverlay).setVisible(false);
    this.mysteryIcon = scene.add.image(0, 0, mysteryIconKey).setDepth(DEPTH.brickOverlay + 0.3).setVisible(false);
    this.sparkle = scene.add
      .graphics()
      .setDepth(DEPTH.brickLabel)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setVisible(false);
    this.markStatus = new BrickMarkStatus(scene);
    this.crack = scene.add.image(0, 0, crackTextureFor(0, 1)).setDepth(DEPTH.brickOverlay).setVisible(false);
    this.label = scene.add
      .text(0, 0, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.display,
        color: PALETTE.textBright,
        stroke: PALETTE.textOutline,
        strokeThickness: 1,
      })
      .setOrigin(0.5)
      .setDepth(DEPTH.brickLabel)
      .setVisible(false);
    this.healthBar = scene.add.graphics().setDepth(DEPTH.brickLabel + 0.1).setVisible(false);
    this.shieldIcon = scene.add.graphics().setDepth(DEPTH.brickLabel).setVisible(false);

    this.nameParts();
    this.richVisuals = [
      this.glow,
      this.glowImage,
      this.pressureDanger,
      this.baseFrame,
      this.overlay,
      this.kindDetail,
      this.mysteryIcon,
      this.sparkle,
      ...this.markStatus.objects,
      this.crack,
      this.label,
      this.healthBar,
      this.shieldIcon,
    ];
    this.baseFrame.on(Phaser.Input.Events.POINTER_DOWN, forwardPointerDown);
    this.mysteryIcon.on(Phaser.Input.Events.POINTER_DOWN, forwardPointerDown);
  }

  private nameParts(): void {
    this.glow.setName("brickGlow");
    this.glowImage.setName("brickGlowImage");
    this.pressureDanger.setName("brickPressureDanger");
    this.baseFrame.setName("brickBaseFrame");
    this.overlay.setName("brickOverlay");
    this.kindDetail.setName("brickKindDetail");
    this.mysteryIcon.setName("brickMysteryIcon");
    this.sparkle.setName("brickSparkle");
    this.crack.setName("brickCrack");
    this.label.setName("brickLabel");
    this.healthBar.setName("brickHealthBar");
    this.shieldIcon.setName("brickShieldIcon");
  }

  forEach(fn: (obj: Phaser.GameObjects.GameObject) => void): void {
    for (const obj of this.richVisuals) fn(obj);
    if (this.mysteryGlyph) fn(this.mysteryGlyph);
  }

  targets(owner: Phaser.GameObjects.GameObject): Phaser.GameObjects.GameObject[] {
    const list = [owner, ...this.richVisuals];
    if (this.mysteryGlyph) list.push(this.mysteryGlyph);
    return list;
  }

  addToDisplayList(): void {
    this.forEach((obj) => {
      const managed = obj as Phaser.GameObjects.GameObject & { displayList?: unknown; addToDisplayList?: () => unknown };
      if (!managed.displayList) managed.addToDisplayList?.();
    });
  }

  removeFromDisplayList(): void {
    this.forEach((obj) => {
      (obj as Phaser.GameObjects.GameObject & { removeFromDisplayList?: () => unknown }).removeFromDisplayList?.();
    });
  }

  syncDisplayList(): void {
    this.forEach((obj) => {
      const managed = obj as Phaser.GameObjects.GameObject & {
        visible?: boolean;
        displayList?: unknown;
        addToDisplayList?: () => unknown;
        removeFromDisplayList?: () => unknown;
      };
      const shouldRender = obj.active && managed.visible !== false;
      if (shouldRender) {
        if (!managed.displayList) managed.addToDisplayList?.();
      } else if (managed.displayList) {
        managed.removeFromDisplayList?.();
      }
    });
  }

  hide(removeFromDisplayList = false): void {
    this.glow.clear().setActive(false).setVisible(false);
    this.glowImage.setActive(false).setVisible(false);
    this.pressureDanger.clear().setAlpha(1).setActive(false).setVisible(false);
    this.baseFrame.setActive(false).setVisible(false);
    this.overlay.setActive(false).setVisible(false);
    this.kindDetail.clear().setActive(false).setVisible(false);
    this.mysteryIcon.setActive(false).setVisible(false);
    this.sparkle.clear().setActive(false).setVisible(false);
    this.markStatus.hideVisuals();
    this.crack.setActive(false).setVisible(false);
    this.label.setActive(false).setVisible(false);
    this.healthBar.clear().setActive(false).setVisible(false);
    this.shieldIcon.clear().setActive(false).setVisible(false);
    this.mysteryGlyph?.setActive(false).setVisible(false);
    if (removeFromDisplayList) this.removeFromDisplayList();
  }

  applyMask(mask: Phaser.Display.Masks.GeometryMask): void {
    this.forEach((obj) => (obj as MaskableGameObject).setMask(mask));
  }

  clearMask(): void {
    this.forEach((obj) => (obj as MaskableGameObject).clearMask());
  }

  destroy(owner: Phaser.GameObjects.GameObject): void {
    this.scene?.tweens?.killTweensOf(this.targets(owner));
    this.forEach((obj) => obj.destroy());
  }
}
